import { z } from "zod";

// ---------------------------------------------------------------------------
// 支払の記録と消込（注文書の3回目 3c、#233、docs/93 §15.5、../eigyouryoku/docs/04 §2.6＝営業力 E5-1 と同じ）。DB 20261026_1700。両製品・門 sales
//   支払＝{vendor}への支払1回（vendor_payments）。どの仕入にいくら当てたか＝配分（vendor_payment_allocations＝消込）。
//   金額（amount）＝相手に渡した額（振込額）。先方負担の振込手数料と源泉徴収は、相手の受け取る額から引いた分として仕入の残を減らす
//   ＝仕入の残 ＝ 合計 − Σ(当てた額 ＋ 先方負担の手数料 ＋ 源泉)。当方負担の手数料はこちらの費用で、仕入の残に関わらない。
//   配分の既定は その{vendor}の残のある仕入に 古い順（支払期日 → 請求日）で上から自動（allocateVendorPayment＝API と画面が同じ関数）。
//   方法は 振込／現金／電子記録債権／一括決済（手形は無い＝取適法で禁止）。額・{vendor}・手数料・源泉は変えない（取消して登録し直す＝入金 E1 と同じ）。
//   言葉：支払・支払登録・振込手数料・先方負担／当方負担・源泉徴収・残（「〜を払う」「払った」を書かない＝docs/10 §5.1.2）
// ---------------------------------------------------------------------------

export const VENDOR_PAYMENT_LIMITS = {
  /** 金額の上限（100 億円未満） */
  amount: 9_999_999_999,
  memo: 2000,
  /** 1回の支払で当てられる仕入の数 */
  allocations: 300,
  /** 履歴に出す件数（{vendor}の詳細・仕入の画面） */
  history: 50,
} as const;

/** 方法（手形は無い） */
export const VendorPaymentMethod = z.enum(["bank_transfer", "cash", "electronic_record_claim", "lump_settlement"]);
export type VendorPaymentMethod = z.infer<typeof VendorPaymentMethod>;
export const VENDOR_PAYMENT_METHOD_LABEL: Record<VendorPaymentMethod, string> = {
  bank_transfer: "振込",
  cash: "現金",
  electronic_record_claim: "電子記録債権",
  lump_settlement: "一括決済",
};

/** 振込手数料の負担：vendor＝先方負担（相手の受け取る額から引く）／own＝当方負担（こちらの費用） */
export const FeeBearer = z.enum(["vendor", "own"]);
export type FeeBearer = z.infer<typeof FeeBearer>;
export const FEE_BEARER_LABEL: Record<FeeBearer, string> = { vendor: "先方負担", own: "当方負担" };

const Yen = z.number().int().min(0).max(VENDOR_PAYMENT_LIMITS.amount);
const YmdZ = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** 配分の1行（API に送る形）。amount＝渡した額のうち当てた分・fee＝先方負担の手数料として引いた分・withholding＝源泉として引いた分 */
export const VendorPaymentAllocationInput = z.object({
  purchaseId: z.string().uuid(),
  amount: Yen,
  fee: Yen.default(0),
  withholding: Yen.default(0),
});
export type VendorPaymentAllocationInput = z.infer<typeof VendorPaymentAllocationInput>;

/** POST /vendor-payments。allocations を省くと その{vendor}の残のある仕入に古い順で自動（allocateVendorPayment） */
export const VendorPaymentCreate = z.object({
  vendorId: z.string().uuid(),
  paidOn: YmdZ,
  amount: Yen.min(1),
  method: VendorPaymentMethod.default("bank_transfer"),
  feeAmount: Yen.default(0),
  feeBearer: FeeBearer.default("vendor"),
  withholdingTaxAmount: Yen.default(0),
  memo: z.string().max(VENDOR_PAYMENT_LIMITS.memo).optional(),
  allocations: z.array(VendorPaymentAllocationInput).max(VENDOR_PAYMENT_LIMITS.allocations).optional(),
});
export type VendorPaymentCreate = z.input<typeof VendorPaymentCreate>;

/** PUT /vendor-payments/:id（版つき。違えば 409 VENDOR_PAYMENT_STALE）。直せるのは 日・方法・メモ だけ（額などは取消して登録し直す） */
export const VendorPaymentUpdate = z.object({
  version: z.string(),
  paidOn: YmdZ.optional(),
  method: VendorPaymentMethod.optional(),
  memo: z.string().max(VENDOR_PAYMENT_LIMITS.memo).nullable().optional(),
});
export type VendorPaymentUpdate = z.infer<typeof VendorPaymentUpdate>;

/** PUT /vendor-payments/:id/allocations（版つき）。配分をこの形に入れ替える（0 の行は作らない） */
export const VendorPaymentAllocationsSave = z.object({
  version: z.string(),
  allocations: z.array(VendorPaymentAllocationInput).max(VENDOR_PAYMENT_LIMITS.allocations),
});
export type VendorPaymentAllocationsSave = z.input<typeof VendorPaymentAllocationsSave>;

/** POST /vendor-payments/:id/void */
export const VendorPaymentVoid = z.object({ version: z.string().optional(), reason: z.string().trim().max(200).default("") });
export type VendorPaymentVoid = z.input<typeof VendorPaymentVoid>;

/** 配分の1行（画面の形）：当てた仕入と、その仕入の今の残 */
export const VendorPaymentAllocation = z.object({
  purchaseId: z.string().uuid(),
  purchasedOn: z.string(),
  vendorInvoiceNo: z.string(),
  paymentDueOn: z.string().nullable(),
  groupName: z.string(),
  /** 仕入の合計（税込） */
  purchaseTotal: z.number().int(),
  /** 仕入の今の残（この支払の分を引いたあと） */
  balance: z.number().int(),
  amount: z.number().int(),
  fee: z.number().int(),
  withholding: z.number().int(),
});
export type VendorPaymentAllocation = z.infer<typeof VendorPaymentAllocation>;

/** 支払の画面の形（GET /vendor-payments/:id） */
export const VendorPayment = z.object({
  id: z.string().uuid(),
  vendor: z.object({ id: z.string().uuid(), name: z.string(), displayName: z.string() }),
  paidOn: z.string(),
  amount: z.number().int(),
  method: VendorPaymentMethod,
  feeAmount: z.number().int(),
  feeBearer: FeeBearer,
  withholdingTaxAmount: z.number().int(),
  /** 当てた額の合計（渡した額のうち）。amount − allocated＝まだ当てていない額 */
  allocated: z.number().int(),
  unallocated: z.number().int(),
  /** 消し込んだ額（Σ 当てた額＋先方負担の手数料＋源泉＝仕入の残を減らした額） */
  settled: z.number().int(),
  allocations: z.array(VendorPaymentAllocation),
  memo: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  /** 版（updated_at。PUT・配分・取消で送り返す） */
  updatedAt: z.string(),
});
export type VendorPayment = z.infer<typeof VendorPayment>;

/** 履歴の1行（{vendor}の詳細・仕入の画面・GET /vendor-payments）。forPurchase＝仕入の画面で見るとき、その仕入に当てた分 */
export const VendorPaymentSummary = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  paidOn: z.string(),
  amount: z.number().int(),
  method: VendorPaymentMethod,
  feeAmount: z.number().int(),
  feeBearer: FeeBearer,
  withholdingTaxAmount: z.number().int(),
  allocated: z.number().int(),
  settled: z.number().int(),
  /** 当てた仕入の数 */
  purchaseCount: z.number().int(),
  /** 当てた仕入の請求書番号（「KM-2610-07 ほか 1件」の頭） */
  firstInvoiceNo: z.string().nullable(),
  forPurchase: z.object({ amount: z.number().int(), fee: z.number().int(), withholding: z.number().int() }).nullable(),
  voided: z.boolean(),
  updatedAt: z.string(),
});
export type VendorPaymentSummary = z.infer<typeof VendorPaymentSummary>;

export const VendorPaymentList = z.object({
  items: z.array(VendorPaymentSummary),
  /** 取消していない支払の額の合計 */
  total: z.number().int(),
});
export type VendorPaymentList = z.infer<typeof VendorPaymentList>;

/** GET /purchases/:id/payments：仕入の画面の節「支払」 */
export const PurchasePayments = z.object({
  purchaseId: z.string().uuid(),
  total: z.number().int(),
  /** 消し込んだ額（当てた額＋先方負担の手数料＋源泉） */
  paid: z.number().int(),
  balance: z.number().int(),
  items: z.array(VendorPaymentSummary),
});
export type PurchasePayments = z.infer<typeof PurchasePayments>;

/** {vendor}の詳細の節「支払」（GET /partners/:id の vendorPayments） */
export const PartnerVendorPayments = z.object({
  items: z.array(VendorPaymentSummary),
  /** 今月の支払（取消していない支払の額の合計） */
  thisMonth: z.object({ count: z.number().int(), total: z.number().int() }),
});
export type PartnerVendorPayments = z.infer<typeof PartnerVendorPayments>;

/** 配分を作る材料（残のある仕入） */
export type OpenPurchase = { purchaseId: string; balance: number; paymentDueOn: string | null; purchasedOn: string };

/** 自動の並び：支払期日の古い順（無い仕入は後ろ）→ 請求日の古い順（DB の v_purchase_balance と同じ言い方で、ここ1か所） */
export function sortForPayment<T extends OpenPurchase>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      (a.paymentDueOn ?? "9999-99-99").localeCompare(b.paymentDueOn ?? "9999-99-99") ||
      a.purchasedOn.localeCompare(b.purchasedOn) ||
      a.purchaseId.localeCompare(b.purchaseId),
  );
}

/** 先方負担の手数料（当方負担なら 0。仕入の残を減らす分） */
export const vendorFeeOf = (fee: number, bearer: FeeBearer): number => (bearer === "vendor" ? Math.max(0, fee) : 0);

/**
 * 消し込む額（仕入の残を減らす額）＝渡した額 ＋ 先方負担の手数料 ＋ 源泉
 */
export const settleOf = (amount: number, fee: number, bearer: FeeBearer, withholding: number): number =>
  Math.max(0, amount) + vendorFeeOf(fee, bearer) + Math.max(0, withholding);

/**
 * 金額の既定（窓を開いたとき・手数料や源泉を打ったとき。金額を打つまで）＝残 − 先方負担の手数料 − 源泉（0 未満は 0）
 */
export const defaultPaymentAmount = (balance: number, fee: number, bearer: FeeBearer, withholding: number): number =>
  Math.max(0, balance - vendorFeeOf(fee, bearer) - Math.max(0, withholding));

/**
 * 消し込む額を仕入に上から埋める（並びはそのまま＝呼ぶ側が sortForPayment で並べる）。仕入ごとの消し込む額（残を超えない）
 */
export function fillSettlements(rows: readonly OpenPurchase[], settle: number): { purchaseId: string; settle: number }[] {
  let rest = Math.max(0, settle);
  return rows.map((r) => {
    const s = Math.max(0, Math.min(r.balance, rest));
    rest -= s;
    return { purchaseId: r.purchaseId, settle: s };
  });
}

/**
 * 仕入ごとの消し込む額を、当てた額・先方負担の手数料・源泉に分ける（手数料 → 源泉の順に、上の行から）。0 の行は除く。
 * 消し込む額の合計が 渡した額＋手数料＋源泉 より少ないとき、渡した額の残りは「まだ当てていない」に残る（前払い・過払い）
 */
export function splitDeductions(settles: readonly { purchaseId: string; settle: number }[], fee: number, withholding: number): VendorPaymentAllocationInput[] {
  let feeRest = Math.max(0, fee);
  let whRest = Math.max(0, withholding);
  const out: VendorPaymentAllocationInput[] = [];
  for (const s of settles) {
    if (s.settle <= 0) continue;
    const f = Math.min(feeRest, s.settle);
    feeRest -= f;
    const w = Math.min(whRest, s.settle - f);
    whRest -= w;
    out.push({ purchaseId: s.purchaseId, amount: s.settle - f - w, fee: f, withholding: w });
  }
  return out;
}

/**
 * 配分の既定（API が allocations を省いた POST で使い、画面の「配分修正」の表の始めの値にも使う＝同じ関数）：
 *   残のある仕入を 支払期日 → 請求日 の古い順に、消し込む額（渡した額＋先方負担の手数料＋源泉）で上から埋め、手数料・源泉は上の行から引く
 */
export function allocateVendorPayment(
  rows: readonly OpenPurchase[],
  x: { amount: number; feeAmount: number; feeBearer: FeeBearer; withholdingTaxAmount: number },
): VendorPaymentAllocationInput[] {
  const fee = vendorFeeOf(x.feeAmount, x.feeBearer);
  const settles = fillSettlements(sortForPayment(rows.filter((r) => r.balance > 0)), settleOf(x.amount, x.feeAmount, x.feeBearer, x.withholdingTaxAmount));
  return splitDeductions(settles, fee, x.withholdingTaxAmount);
}

/** 画面と API の文（{vendor} は会社の言葉＝画面は t()、API は fillLabels） */
export const VENDOR_PAYMENT_MSG = {
  section: "支払",
  create: "支払登録",
  createShort: "支払登録",
  createAll: "支払登録",
  dialogTitle: "支払登録",
  editTitle: "支払の修正",
  paidOn: "支払日",
  amount: "金額",
  method: "方法",
  fee: "振込手数料",
  feeBearer: "負担",
  withholding: "源泉徴収",
  memo: "メモ",
  balance: "残",
  settled: "消込額",
  allocated: "配分",
  unallocated: "未配分",
  allocate: "配分修正",
  allocateAuto: "古い順に配分",
  allocateTitle: "配分",
  colPurchase: "仕入",
  colBillDate: "請求日",
  colInvoiceNo: "請求書番号",
  colDue: "支払期日",
  colBalance: "残",
  colSettle: "消込額",
  colPaidOn: "支払日",
  colAmount: "金額",
  colMethod: "方法",
  colFee: "振込手数料",
  colWithholding: "源泉徴収",
  colTarget: "配分",
  colThis: "この仕入",
  history: "支払の履歴",
  historyEmpty: "支払の記録はありません",
  payableTotal: "未払い",
  thisMonth: "今月の支払",
  voidMark: "取消",
  menu: (date: string) => `${date} の支払の操作`,
  edit: "修正",
  void: "取消",
  voidTitle: "支払の取消",
  voidConfirm: "この支払を取消します。配分した仕入の残額が元に戻ります。",
  voidReason: "理由（任意）",
  created: "支払を登録しました。",
  updated: "支払を修正しました。",
  voided: "支払を取消しました。",
  save: "支払登録",
  saveEdit: "保存",
  restAfter: (n: string) => `登録後の残額 ${n} 円`,
  restNote: (n: string) => `未配分 ${n} 円は、どの仕入にも消込せずに残ります`,
  helpWithholding: "源泉徴収は、個人への設計料・報酬などで、支払額から差し引いて税務署に納める額です。差し引いた額は仕入の残額を減らします。",
  helpFee: "先方負担：振込手数料を差し引いて振り込みます（差し引いた額は仕入の残額を減らします）。当方負担：手数料はこちらの費用で、仕入の残額は振り込んだ額だけ減ります。",
  helpAmount: "相手に振り込む額（受け取る額）です。既定は 残 − 先方負担の振込手数料 − 源泉徴収 です。",
  helpAllocate: "支払期日の古い順に自動で配分します。配分を修正すると、仕入ごとの消込額を打てます。",
  helpHistory: "取消した支払は「取消」で残ります（残額の計算には含めません）。",
  notFound: "その支払は見つかりません",
  stale: "他の利用者が変更しました。再読込しますか？",
  amountInvalid: "金額は 1 円以上で入力してください",
  amountFixed: "支払額・{vendor}・振込手数料・源泉徴収は変更できません。取消して登録し直してください",
  futureDate: "支払日は本日までの日付で入力してください",
  methodInvalid: "支払の方法を選んでください",
  vendorInvalid: "支払の相手は{vendor}だけです",
  overAllocated: "配分の合計が支払の額を超えています。配分を減らしてください",
  overPurchase: "仕入の残額を超えて消込しています。配分を減らしてください",
  feeOwn: "当方負担の振込手数料は仕入の残額を減らしません",
  purchaseNotConfirmed: "支払は確定した仕入にのみ消込できます",
  purchaseOtherVendor: "ほかの{vendor}の仕入には消込できません",
  purchaseNotFound: "その仕入は見つかりません",
  voidedAlready: "この支払は取消されています",
  purchaseHasPayment: "支払の記録がある仕入は取消できません。先に支払を取消してください",
  purchaseHasPaymentUnconfirm: "支払の記録がある仕入は下書きに戻せません。先に支払を取消してください",
  /** 仕入の「⋯」の押せない項目の下の1行 */
  purchaseHasPaymentNote: "支払を取消すと操作できます",
  noOpen: "残のある仕入はありません",
} as const;
